/**
 * Franchise matchers for ranked lists. A "top N" that lists KITT twice or
 * three Mario sounds reads as padding, so a ranked list keeps the
 * highest-ranked sound per franchise and moves on.
 */
const FRANCHISES: ReadonlyArray<{ key: string; pattern: RegExp }> = [
  { key: 'kitt', pattern: /\bkitt\b|knight rider/i },
  { key: 'transformers', pattern: /\btransformers?\b|\bautobots?\b/i },
  { key: 'mario', pattern: /\bmario\b/i },
  { key: 'tron', pattern: /\btron\b/i },
  { key: 'star-wars', pattern: /star wars|lightsaber|imperial march|darth vader|chewbacca|r2-d2/i },
  { key: 'star-trek', pattern: /star trek|uss enterprise|ncc-1701/i },
  { key: 'iron-man', pattern: /\bjarvis\b|iron man/i },
  { key: 'zelda', pattern: /\bzelda\b/i },
  { key: 'pokemon', pattern: /pok[eé]mon|pikachu/i },
]

export function franchiseOf(title: string): string | null {
  return FRANCHISES.find(({ pattern }) => pattern.test(title))?.key ?? null
}

export interface SeenSounds {
  ids: Set<string>
  franchises: Set<string>
}

export function newSeen(): SeenSounds {
  return { ids: new Set(), franchises: new Set() }
}

/**
 * Takes up to `limit` sounds, skipping any id or franchise already in `seen`,
 * and records what it took. Sharing one `seen` across several lists keeps a
 * page from repeating a sound or a franchise between them.
 */
export function takeDistinct<T extends { id?: string; title: string }>(
  candidates: T[],
  limit: number,
  seen: SeenSounds,
): T[] {
  const kept: T[] = []
  for (const sound of candidates) {
    if (kept.length === limit) break
    const franchise = franchiseOf(sound.title)
    if ((sound.id && seen.ids.has(sound.id)) || (franchise && seen.franchises.has(franchise))) continue
    if (sound.id) seen.ids.add(sound.id)
    if (franchise) seen.franchises.add(franchise)
    kept.push(sound)
  }
  return kept
}

/** Keeps the first (highest-ranked) sound of each franchise, up to `limit`. */
export function dedupeByFranchise<T extends { id?: string; title: string }>(sounds: T[], limit: number): T[] {
  return takeDistinct(sounds, limit, newSeen())
}

export interface BestOfSectionSpec {
  label: string
  href: string
  categories: ReadonlySet<string>
  count: number
}

/**
 * The top list first, then each themed section filled from its own category
 * pool, skipping anything already used on the page. `fillOrder` lists section
 * labels from most to least specific: the narrow pools claim their sounds
 * before broad ones (Memes, Cinematic) can. Output keeps the `specs` order.
 * A section is empty only when its whole pool is used up.
 */
export function buildBestOfPage<T extends { id?: string; title: string; category: string }>(
  pool: T[],
  specs: BestOfSectionSpec[],
  fillOrder: string[],
  topCount: number,
): { top: T[]; sections: Array<{ label: string; href: string; sounds: T[] }> } {
  const seen = newSeen()
  const top = takeDistinct(pool, topCount, seen)
  const filled = new Map<string, T[]>()
  for (const label of fillOrder) {
    const spec = specs.find((candidate) => candidate.label === label)
    if (!spec) continue
    filled.set(label, takeDistinct(pool.filter((s) => spec.categories.has(s.category)), spec.count, seen))
  }
  return {
    top,
    sections: specs.map(({ label, href }) => ({ label, href, sounds: filled.get(label) ?? [] })),
  }
}

/** Under one second is a clipped or unmeasured value; showing "0:00" misleads. */
export function formatSoundDuration(seconds: number | null | undefined): string | null {
  if (!seconds || seconds < 1) return null
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
